import useFetcher from "@/utils/use-fetcher";
import {
  getGetLineSheetTabV2Key,
  GetLineSheetTabV2Request,
  GetLineSheetTabV2Response,
} from "@/features/line-sheet-sets/hooks/use-get-line-sheet-tab-v2";
import { useMemo } from "react";
import { AxiosRequestConfig } from "axios";
import { SheetProduct, SheetTab } from "@/features/order-sheets/sheet.type";
import {
  getOrderSheetRevisionV2Key,
  GetOrderSheetRevisionV2Request,
  parseGetOrderSheetRevisionV2Key,
} from "./use-get-order-sheet-revision-v2";
import { createOrderSheetProductFinder } from "@/features/line-sheet-sets/helpers/order-sheet-product-finder";

export interface GetListAllSheetTabsWithRevisionV2Response {
  sheetTabs: SheetTab[];
  _orderSheetSetId: number;
}
export interface GetListOrderSheetTabRevisionV2Response {
  createdAt: Date;
  createdBy: {
    id: number;
    name: string;
  };
  createdByCompany: {
    id: number;
    name: string;
  };
  orderSheetTabs: SheetTab[];
  revisionNumber: number;
}

export default function useListAllSheetTabsAndRevisionV2Fetcher() {
  const getLineSheetTabFetcherV2 = useFetcher<
    GetLineSheetTabV2Request,
    GetLineSheetTabV2Response
  >();
  const getOrderSheetRevisionFetcherV2 = useFetcher<
    GetOrderSheetRevisionV2Request,
    GetListOrderSheetTabRevisionV2Response
  >();

  return useMemo(() => {

    return async (
      config: AxiosRequestConfig<GetOrderSheetRevisionV2Request>
    ): Promise<GetListAllSheetTabsWithRevisionV2Response> => {
      const { parameter, request } = parseGetOrderSheetRevisionV2Key(config);
      if (
        !parameter.companyId ||
        parameter.companyId < 0 ||
        !parameter.orderSheetSetId ||
        parameter.orderSheetSetId < 0 ||
        !parameter.revisionNumber ||
        parameter.revisionNumber < 0
      ) {
        const sheetTabs: SheetTab[] = [];
        return Promise.resolve({
          _orderSheetSetId: parameter.orderSheetSetId,
          sheetTabs,
        });
      }

      const lineSheetTabV2Responses = await Promise.all(
        [
          getLineSheetTabFetcherV2(
            getGetLineSheetTabV2Key({
              by: parameter.by,
              companyId: parameter.companyId,
              lineSheetSetId: request.lineSheetSetId,
            })!!
          ),
        ]
      );
      const orderSheetTabsV2Response = await Promise.resolve(
        getOrderSheetRevisionFetcherV2(
          getOrderSheetRevisionV2Key(
            {
              by: parameter.by,
              companyId: parameter.companyId,
              orderSheetSetId: parameter.orderSheetSetId,
              revisionNumber: parameter.revisionNumber,
            },
            request
          )
        )
      );

      const sheetTabs = [];

      for (let i = 0; i < lineSheetTabV2Responses.length; i += 1) {
        const lineSheetTab = lineSheetTabV2Responses[
          i
        ] as GetLineSheetTabV2Response;
        const orderSheetTab =
          orderSheetTabsV2Response.orderSheetTabs[lineSheetTab.index];

        // 두 목록은 같은 순번끼리가 아니라 상품 id 로 짝짓는다 — 상품 수가 다르면(재고가 전부 0 인
        // 상품은 바이어의 라인시트 목록에서만 빠진다) 순번이 밀려 남의 공급가·주문 수량이 붙는다.
        const findOrderSheetProduct = createOrderSheetProductFinder(
          orderSheetTab.products
        );
        const sheetProducts = lineSheetTab.products.map(
          (lineSheetProduct, index): SheetProduct => {
            const orderSheetProduct = findOrderSheetProduct(
              lineSheetProduct,
              index
            );
            if (orderSheetProduct) {
              return {
                ...lineSheetProduct,
                isLiked: orderSheetProduct.isLiked,
                supplyPrice: orderSheetProduct.supplyPrice,
                priceAdjustedBy: orderSheetProduct.priceAdjustedBy,
                orderQuantityWithOptionList:
                  orderSheetProduct.orderQuantityWithOptionList,
                confirmedPriceAdjustedBy:
                  orderSheetProduct.confirmedPriceAdjustedBy,
                confirmedPrice: orderSheetProduct.confirmedPrice,
                confirmedPriceWithoutCondition: orderSheetProduct.confirmedPriceWithoutCondition,
                newStockWithOptionList:
                  orderSheetProduct.newStockWithOptionList,
                confirmedQuantityWithOptionList:
                  orderSheetProduct.confirmedQuantityWithOptionList,
                firstOrderQuantityWithOptionList:
                  orderSheetProduct.firstOrderQuantityWithOptionList,
                prevOrderQuantityWithOptionList:
                  orderSheetProduct.prevOrderQuantityWithOptionList,
                latestOrderQuantityWithOptionList:
                  orderSheetProduct.latestOrderQuantityWithOptionList,
              };
            } else {
              return {
                ...lineSheetProduct,
                isLiked: false,
                orderQuantityWithOptionList: [],
                confirmedPriceAdjustedBy: null,
                confirmedPrice: null,
                confirmedPriceWithoutCondition: null,
                newStockWithOptionList: null,
                confirmedQuantityWithOptionList: null,
                firstOrderQuantityWithOptionList: null,
                prevOrderQuantityWithOptionList: null,
                latestOrderQuantityWithOptionList: null,
              };
            }
          }
        );

        sheetTabs.push({
          index: lineSheetTab.index,
          name: lineSheetTab.name,
          products: sheetProducts,
        });
      }

      return Promise.resolve({
        _orderSheetSetId: parameter.orderSheetSetId,
        sheetTabs,
      });
    };
  }, [getOrderSheetRevisionFetcherV2, getLineSheetTabFetcherV2]);
}
